import { useMemo } from 'react';
import { Platform } from 'react-native';
import { useTheme, useThemeName } from 'tamagui';
import { colors, colorsDark } from '../theme/colors';
import type { ThemeColors } from '../theme/colors';

export type ThemeColorValues = ThemeColors & {
  /**
   * Concrete hex / rgba values for the active theme — for color MATH only,
   * where the top-level CSS-variable values can't be used:
   *   - reanimated `interpolateColor(...)` (interpolates RGB channels)
   *   - manual alpha suffixing (`color + '14'`)
   *   - canvas / charting / color-library APIs
   *
   * These are JS-resolved and so lag the CSS theme class by a frame on web
   * hydration — fine for those use cases (scroll-driven animations and faint
   * translucent fills, never load-critical text contrast). For plain inline
   * styles and `<Icon color>`, use the top-level values, which never desync.
   */
  raw: ThemeColors;
};

/**
 * Theme color values for React Native inline styles / StyleSheet and
 * `<Icon color>` — anywhere a Tamagui `$token` style prop isn't available
 * (RN `Pressable`/`Animated.View` inline styles, react-native-svg color
 * props).
 *
 * On **web** the top-level values are Tamagui CSS *variable* references
 * (`var(--…)`) — the exact same source the `$token` style props compile to.
 * Inline styles and `$token` styles therefore resolve from one place and flip
 * together the instant the theme class changes on the document. This removes
 * the hydration-time desync that used to flash illegible contrast: the static
 * web export prerenders light, and on a dark device the Tamagui theme class
 * (CSS) flipped to dark a frame before the JS-resolved hex caught up, so a
 * `theme.*` fill and a `$token` label briefly disagreed about the theme. With
 * CSS variables on both sides there is nothing to get out of sync.
 *
 * On **native** the top-level values are concrete colors (CSS variables don't
 * exist there). Native gates first paint on the persisted theme, so it never
 * had the desync — behaviour is unchanged.
 *
 * ⚠️ The top-level web values are opaque `var(--…)` strings, so you CANNOT do
 * color math on them (`color + '14'`, `interpolateColor`). For those, read the
 * concrete values from `theme.raw` (see {@link ThemeColorValues.raw}).
 */
export function useThemeColors(): ThemeColorValues {
  // Subscribe to theme changes so consumers re-render on toggle. On native
  // this is what propagates a new color; on web the CSS variables update on
  // their own, so the re-render just re-applies an identical `var(--…)`. The
  // name also selects the concrete `raw` palette.
  const name = useThemeName();
  const theme = useTheme();
  // Memoise on the active theme so the returned object keeps a stable identity
  // across renders — it did when it was a module constant, and some consumers
  // pass the whole object as a prop or memo dependency. `useTheme()` is cached
  // per theme, so this rebuilds only on a theme switch.
  return useMemo(() => {
    const rawPalette = name === 'dark' ? colorsDark : colors;
    // Native has no CSS variables, so the top-level values ARE the concrete
    // palette colors — return them in one shot. We must NOT use
    // `theme[key].get()` on native: in this Tamagui version it can return a
    // dynamic, theme-reactive *object* rather than a string, which then throws
    // when passed as a `color` into react-native-reanimated's color processor
    // (`Invalid color value: [object Object]`).
    if (Platform.OS !== 'web') {
      return { ...rawPalette, raw: rawPalette } as ThemeColorValues;
    }
    const out: Record<string, unknown> = { raw: rawPalette };
    for (const key of Object.keys(colors)) {
      const entry = (theme as Record<string, { get?: () => string } | undefined>)[key];
      // `.get()` returns the CSS variable on web (unlike reading `.val`, it
      // registers no re-render dependency). Keys Tamagui has no variable for —
      // e.g. the non-string `polaroidRainbow` ramp — fall back to the palette.
      out[key] =
        entry && typeof entry.get === 'function'
          ? entry.get()
          : (colors as Record<string, unknown>)[key];
    }
    return out as ThemeColorValues;
  }, [name, theme]);
}
